import React, { useState } from "react";
import { Z } from "../../../theme/zIndex";
import {
  Box,
  Typography,
  CardContent,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Grid,
  Stack,
  Alert,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format, differenceInDays } from "date-fns"; // 1. IMPORTAR PARSE
import { es } from "date-fns/locale";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BadgeIcon from "@mui/icons-material/Badge";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import NotesIcon from "@mui/icons-material/Notes";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PaidIcon from "@mui/icons-material/Paid"; // Icono para "Cobrado por"
import { formatCurrency } from "../../shared/utils/formatters";
import { ReservaEstacionamientoDisplay } from "../../../types/ReservaEstacionamiento";
import { UserPermissions } from "../../auth/hooks/usePermissions";
import { useEditRestriction } from "../../auth/hooks/useEditRestriction";
import RestrictionModal from "../../auth/components/RestrictionModal";
import { logger } from "../../../utils/logger";
import AppDialog from "../../shared/components/AppDialog";
import { useCloseOnBack } from "../../shared/hooks/useCloseOnBack";
import { useDeleteEstacionamientoReservation } from "../../../hooks/useEstacionamientoMutations";
import { parseDateOnly } from "../../shared/utils/dateUtils";

interface EstacionamientoDetailsProps {
  reserva: ReservaEstacionamientoDisplay | null;
  onClose: () => void;
  onEdit: (reserva: ReservaEstacionamientoDisplay) => void;
  permissions: UserPermissions;
}

const EstacionamientoDetails: React.FC<EstacionamientoDetailsProps> = ({
  reserva,
  onClose,
  onEdit,
  permissions,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deleteMutation = useDeleteEstacionamientoReservation();

  const {
    isRestrictionModalOpen,
    handleEditAttempt,
    closeRestrictionModal,
    restrictionMessage,
  } = useEditRestriction();

  useCloseOnBack(Boolean(reserva), onClose);

  const handleEditClick = () => {
    const canEdit = handleEditAttempt();
    if (canEdit && reserva) {
      onEdit(reserva);
    }
  };

  if (!reserva) {
    return null;
  }

  const handleDelete = async () => {
    if (!reserva) return;
    setError(null);
    try {
      await deleteMutation.mutateAsync(reserva.id);
      setConfirmDelete(false);
      onClose(); // Cierra el modal de detalles
    } catch (e: unknown) {
      const message =
        e &&
        typeof e === "object" &&
        "response" in e &&
        e.response &&
        typeof e.response === "object" &&
        "data" in e.response &&
        e.response.data &&
        typeof e.response.data === "object" &&
        "error" in e.response.data &&
        typeof e.response.data.error === "string"
          ? e.response.data.error
          : "Error al eliminar la reserva";
      setError(message);
    }
  };

  // Corregir el parseo de fechas usando los campos correctos y el formato adecuado
  const fechaEntrada = parseDateOnly(reserva.entrada);
  const fechaSalida = parseDateOnly(reserva.salida);

  // Manejar el caso de que el parseo falle
  if (isNaN(fechaEntrada.getTime()) || isNaN(fechaSalida.getTime())) {
    logger.error(
      "Error al parsear las fechas de la reserva de estacionamiento",
      reserva
    );
    return (
      <Alert severity="error">
        Las fechas de esta reserva son inválidas. Por favor, revise los datos.
      </Alert>
    );
  }

  const cantidadNoches = Math.max(
    differenceInDays(fechaSalida, fechaEntrada),
    0
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={!!reserva}
        onClose={onClose}
        sx={{ zIndex: Z.drawer }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            maxWidth: "100vw",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 1, sm: 2 },
            pt: { xs: 0.5, sm: 2 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 700 }}>
              Reserva Cochera #{reserva.id}
            </Typography>
            <Chip
              label={reserva.estado ? reserva.estado.toUpperCase() : "CONFIRMADA"}
              size="small"
              color={reserva.estado === "confirmada" ? "success" : "warning"}
            />
          </Stack>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Contenido principal */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            p: { xs: 1, sm: 2 },
          }}
        >
          {/* Info de Cochera */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <DirectionsCarIcon color="primary" />
            <Typography variant="body1" fontWeight={500}>
              Cochera {reserva.estacionamiento}
            </Typography>
            {reserva.departamento_asociado && (
              <Chip
                icon={<ApartmentIcon />}
                label={`Depto ${reserva.departamento_asociado}`}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Fechas */}
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon fontSize="small" color="primary" />
                <Typography variant="body2">
                  {format(fechaEntrada, "dd/MM/yyyy", { locale: es })} -{" "}
                  {format(fechaSalida, "dd/MM/yyyy", { locale: es })}
                </Typography>
              </Stack>
              {/* Se elimina la línea de Check-in / Check-out */}
              <Typography variant="body2" color="text.secondary">
                {cantidadNoches} {cantidadNoches === 1 ? "noche" : "noches"}
              </Typography>
            </Stack>
          </CardContent>

          <Divider sx={{ my: 1 }} />

          {/* Titular de la reserva (solo si hay datos) */}
          {(reserva.nombre || reserva.apellido || reserva.dni) && (
            <>
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight={500}>
                      Datos del Huésped
                    </Typography>
                  </Stack>
                  {(reserva.nombre || reserva.apellido) && (
                    <Typography variant="body2" color="text.secondary" ml={3}>
                      {reserva.nombre} {reserva.apellido}
                    </Typography>
                  )}
                  {reserva.dni && (
                    <Stack direction="row" spacing={1} alignItems="center" ml={3}>
                      <BadgeIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        DNI: {reserva.dni}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Notas */}
          {reserva.notas && (
            <>
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <NotesIcon fontSize="small" color="primary" />
                    <Typography variant="body2" fontWeight={500}>
                      Notas
                    </Typography>
                  </Stack>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: "grey.50" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {reserva.notas}
                    </Typography>
                  </Paper>
                </Stack>
              </CardContent>
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {/* Información de pago */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              mt: 1,
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Valor Total:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(reserva.valor_total || 0)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Seña:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(reserva.pago || 0)}
                </Typography>
              </Stack>
              <Divider sx={{ my: 0.5 }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2">Saldo Pendiente:</Typography>
                <Chip
                  label={formatCurrency(
                    (reserva.valor_total || 0) - (reserva.pago || 0)
                  )}
                  color={
                    (reserva.valor_total || 0) - (reserva.pago || 0) > 0
                      ? "error"
                      : "success"
                  }
                  size="small"
                />
              </Stack>
              {/* Mostrar quién cobró */}
              {reserva.cobrado_por && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">Cobrado por:</Typography>
                    <Chip
                      icon={<PaidIcon />}
                      label={reserva.cobrado_por}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>
        </Box>

        {/* Botones de acción */}
        <Box
          sx={{
            p: { xs: 1, sm: 2 },
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<EditIcon fontSize="small" />}
                onClick={handleEditClick}
                size="small"
                sx={{ minHeight: 40 }}
              >
                Editar
              </Button>
            </Grid>
            {permissions.canDeleteReservations && (
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={() => setConfirmDelete(true)}
                  size="small"
                  sx={{ minHeight: 40 }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Drawer>

      {/* Diálogo de confirmación de eliminación */}
      <AppDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        sx={{ zIndex: Z.modal }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Confirmar eliminación
          </Typography>
          <Typography sx={{ mb: 3 }}>
            ¿Estás seguro de que deseas eliminar esta reserva de cochera?
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setConfirmDelete(false)} disabled={deleteMutation.isPending}>Cancelar</Button>
            <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </Box>
        </Box>
      </AppDialog>

      {/* Modal de restricción de edición */}
      <RestrictionModal
        open={isRestrictionModalOpen}
        onClose={closeRestrictionModal}
        message={restrictionMessage}
        contactPerson="Mariela Mendes Nunes"
      />
    </>
  );
};

export default EstacionamientoDetails;
